import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Download, FileText, Users, Clock, Activity, AlertTriangle, ShieldCheck } from 'lucide-react';
import { LANGUAGES } from '../lib/languagesCatalog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const langName = (code) => LANGUAGES.find((l) => l.code === code)?.name || (code || '').toUpperCase();

const STATE_LABELS = {
  subtitle_received: 'Legenda recebida',
  subtitle_rendered: 'Legenda renderizada',
  audio_received: 'Áudio recebido',
  playback_started: 'Reprodução iniciada',
  playback_completion: 'Reprodução concluída',
};

const AVAIL_COLORS = { normal: 'bg-emerald-500', degraded: 'bg-amber-500', critical: 'bg-red-500', suspended: 'bg-slate-500', inactive: 'bg-slate-600' };

const Section = ({ title, icon: Icon, children, testId }) => (
  <Card className="bg-slate-900 border-slate-800" data-testid={testId}>
    <CardHeader className="pb-3">
      <CardTitle className="font-['Manrope'] text-base text-white flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-cyan-400" />}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const Pct = ({ v }) => (v === null || v === undefined ? <span className="text-slate-500">—</span> : <span>{v}%</span>);
const Num = ({ v, suffix = '' }) => (v === null || v === undefined ? <span className="text-slate-500">—</span> : <span>{v}{suffix}</span>);

export default function ReportView() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/sessions/${sessionId}/report`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReport(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Erro ao carregar o relatório');
      }
    };
    load();
  }, [sessionId]);

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio_${report?.session?.code || sessionId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <Card className="bg-slate-900 border-slate-800 max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
            <p className="text-slate-300 text-sm" data-testid="report-error">{error}</p>
            <Button onClick={() => navigate('/host')} variant="outline" data-testid="report-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!report) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400" data-testid="report-loading">A gerar relatório...</div>;
  }

  const s = report.session || {};
  const acks = report.acks || {};
  const avail = report.availability || {};

  return (
    <div className="min-h-screen bg-slate-950 p-4 sm:p-6" data-testid="report-page">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/host')} className="text-slate-400" data-testid="report-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-white">Relatório Pós-Evento</h1>
              <p className="text-xs text-slate-500">{s.name} · {s.code} · gerado {new Date(report.generated_at).toLocaleString('pt-PT')}</p>
            </div>
          </div>
          <Button onClick={downloadJson} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="report-download-json-btn">
            <Download className="w-4 h-4 mr-2" /> Descarregar JSON
          </Button>
        </div>

        {/* Sumário executivo */}
        <Card className="bg-gradient-to-br from-slate-900 to-slate-900/60 border-cyan-900/50" data-testid="report-exec-summary">
          <CardHeader className="pb-3">
            <CardTitle className="font-['Manrope'] text-lg text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-cyan-400" /> Sumário Executivo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {(report.executive_summary?.linhas || []).map((l, i) => (
                <li key={i} className="text-sm text-slate-200 flex gap-2">
                  <span className="text-cyan-500 mt-0.5">•</span> {l}
                </li>
              ))}
            </ul>
            {acks.available && acks.disclosure && (
              <p className="mt-4 text-[11px] text-slate-500 italic border-t border-slate-800 pt-3" data-testid="ack-disclosure">
                {acks.disclosure}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Anexo técnico */}
        <h2 className="font-['Manrope'] text-sm uppercase tracking-widest text-slate-500 pt-2">Anexo Técnico</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Section title="Sessão" icon={Clock} testId="report-session-info">
            <div className="space-y-1.5 text-xs text-slate-300">
              <p>Início: <span className="text-white">{s.created_at ? new Date(s.created_at).toLocaleString('pt-PT') : '—'}</span></p>
              <p>Fim: <span className="text-white">{s.ended_at ? new Date(s.ended_at).toLocaleString('pt-PT') : '—'}</span></p>
              <p>Duração: <span className="text-white">{report.duration_minutes != null ? `${report.duration_minutes} min` : '—'}</span></p>
              <p>Origem: <span className="text-white">{s.bilateral ? `Bilateral (${(s.spoken_langs || []).map(c => c.toUpperCase()).join(' ⇄ ')})` : langName(s.source_lang)}</span></p>
              <p>Destinos: <span className="text-white">{(s.target_langs || []).map(langName).join(', ')}</span></p>
              {s.is_demo && <p className="text-orange-400 font-semibold">Sessão DEMO</p>}
            </div>
          </Section>

          <Section title="Participantes" icon={Users} testId="report-participants">
            <div className="space-y-1.5 text-xs text-slate-300">
              <p>Pico de participantes: <span className="text-white text-lg font-bold">{report.participants?.peak ?? '—'}</span></p>
              {acks.available && (
                <>
                  <p>Ligações (média nas janelas): <Num v={acks.sampling?.avg_connected} /></p>
                  <p>Amostra técnica (média): <Num v={acks.sampling?.avg_sample_size} /></p>
                </>
              )}
            </div>
          </Section>

          <Section title="Segmentos" icon={Activity} testId="report-segments">
            <div className="space-y-1.5 text-xs text-slate-300">
              <p>Total processados: <span className="text-white text-lg font-bold">{report.segments?.total ?? 0}</span></p>
              <p>Caracteres (origem): <Num v={report.segments?.source_chars} /></p>
              {report.segments?.by_detected_lang && Object.keys(report.segments.by_detected_lang).length > 0 && (
                <p>Por idioma falado: {Object.entries(report.segments.by_detected_lang).map(([l, n]) => `${l.toUpperCase()} ${n}`).join(' · ')}</p>
              )}
            </div>
          </Section>
        </div>

        {/* Latências por idioma */}
        <Section title="Latências por idioma (pipeline)" icon={Activity} testId="report-latencies">
          {Object.keys(report.latencies || {}).length === 0 ? (
            <p className="text-xs text-slate-500">Sem métricas de pipeline registadas.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 border-b border-slate-800">
                    <th className="text-left py-2 pr-4">Idioma</th>
                    <th className="text-right px-2">Tradução p50/p95</th>
                    <th className="text-right px-2">TTS p50/p95</th>
                    <th className="text-right px-2">Total p50/p95</th>
                    <th className="text-right pl-2">Amostras</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(report.latencies).map(([lang, d]) => (
                    <tr key={lang} className="border-b border-slate-800/50 text-slate-300">
                      <td className="py-2 pr-4 text-white">{langName(lang)}</td>
                      <td className="text-right px-2"><Num v={d.translation?.p50_s} suffix="s" /> / <Num v={d.translation?.p95_s} suffix="s" /></td>
                      <td className="text-right px-2"><Num v={d.tts?.p50_s} suffix="s" /> / <Num v={d.tts?.p95_s} suffix="s" /></td>
                      <td className="text-right px-2"><Num v={d.total?.p50_s} suffix="s" /> / <Num v={d.total?.p95_s} suffix="s" /></td>
                      <td className="text-right pl-2">{d.total?.count ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* Disponibilidade */}
        <Section title="Disponibilidade (% do tempo observado por estado)" icon={ShieldCheck} testId="report-availability">
          {!avail.available ? (
            <p className="text-xs text-slate-500" data-testid="availability-insufficient">{avail.reason || 'Dados insuficientes.'}</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(avail.scopes || {}).map(([scope, d]) => d && (
                <div key={scope}>
                  <p className="text-xs text-slate-400 mb-1">{scope === 'global' ? 'Global' : langName(scope)} <span className="text-slate-600">({d.observed_seconds}s observados)</span></p>
                  <div className="flex h-3 rounded-full overflow-hidden bg-slate-800">
                    {Object.entries(d.pct).map(([st, p]) => (
                      <div key={st} className={`${AVAIL_COLORS[st] || 'bg-slate-600'}`} style={{ width: `${p}%` }} title={`${st}: ${p}%`} />
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">{Object.entries(d.pct).map(([st, p]) => `${st} ${p}%`).join(' · ')}</p>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Métricas ACK */}
        <Section title="Métricas de entrega e reprodução (ACK)" icon={Activity} testId="report-acks">
          {!acks.available ? (
            <p className="text-xs text-slate-500" data-testid="acks-unavailable">{acks.reason || 'Sem dados de ACK.'}</p>
          ) : (
            <div className="space-y-4">
              <p className="text-[11px] text-slate-500 italic">{acks.disclosure}</p>
              {Object.entries(acks.langs || {}).map(([lang, states]) => (
                <div key={lang}>
                  <p className="text-xs text-white font-semibold mb-1">{langName(lang)}</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-500 border-b border-slate-800">
                          <th className="text-left py-1.5 pr-4">Estado</th>
                          <th className="text-right px-2">Taxa (mediana)</th>
                          <th className="text-right px-2">Latência p50</th>
                          <th className="text-right px-2">Latência p95</th>
                          <th className="text-right pl-2">Janelas c/ dados</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(states).map(([key, st]) => (
                          <tr key={key} className="border-b border-slate-800/40 text-slate-300">
                            <td className="py-1.5 pr-4">{STATE_LABELS[key] || key}</td>
                            <td className="text-right px-2">
                              {st.insufficient_data ? <span className="text-slate-500" title="Dados insuficientes">dados insuf.</span> : <Pct v={st.rate_median_pct} />}
                            </td>
                            <td className="text-right px-2"><Num v={st.latency_p50_median_s} suffix="s" /></td>
                            <td className="text-right px-2"><Num v={st.latency_p95_median_s} suffix="s" /></td>
                            <td className="text-right pl-2">{st.windows_with_data}/{st.windows_with_data + st.windows_without_data}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              <div className="border-t border-slate-800 pt-2 space-y-1">
                {(acks.notes || []).map((n, i) => (
                  <p key={i} className="text-[11px] text-slate-500">• {n}</p>
                ))}
              </div>
            </div>
          )}
        </Section>

        {/* Ações operacionais + erros */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Section title="Ações operacionais" icon={ShieldCheck} testId="report-ops">
            {(report.operational_actions || []).length === 0 ? (
              <p className="text-xs text-slate-500">Nenhuma ação registada.</p>
            ) : (
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {report.operational_actions.map((o, i) => (
                  <p key={i} className="text-[11px] text-slate-400">
                    <span className="text-slate-600">{o.timestamp ? new Date(o.timestamp).toLocaleTimeString('pt-PT') : ''}</span>{' '}
                    <span className="text-slate-200">{o.action}</span>{o.lang ? ` · ${o.lang.toUpperCase()}` : ''} <span className="text-slate-600">({o.user})</span>
                  </p>
                ))}
              </div>
            )}
          </Section>

          <Section title="Erros" icon={AlertTriangle} testId="report-errors">
            <p className="text-xs text-slate-300 mb-2">Total: <span className="text-white font-bold">{report.errors?.count ?? 0}</span></p>
            {(report.errors?.recent || []).slice(0, 8).map((e, i) => (
              <p key={i} className="text-[11px] text-red-400/80 truncate">{e.event || e.stage || 'erro'}: {e.error || e.message}</p>
            ))}
          </Section>
        </div>
      </div>
    </div>
  );
}
